import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { List, ListTree, Map, QrCode, FileStack, CheckCircle2 } from 'lucide-react'
import type { RecoverRequest } from '../api'

/**
 * The per-row actions from the Queue, so a row means the same thing on every view.
 *
 * They were only on the Queue table, which made the other views read-only lookups — you could see
 * that an object needed an engineer but not open its runbook, its details or its floor plan
 * without switching back and finding the row again. Same six actions, same order, same icons.
 */
export interface RowActionHandlers {
  onShowRunbook?: (objectID: string, scenario: number | null, forecastStartTime: string | Date | null) => void
  onShowObjectDetails?: (objectID: string, requestID: string) => void
  onShowFloorPlan?: (objectID: string) => void
  onShowQRCode?: (objectID: string) => void
  onShowSubObjectProgress?: (mainId: string, objectDescription?: string | null) => void
  onMarkComplete?: (objectID: string, objectDescription: string | null, recoverRequestId: number | null) => void
}

export function RowActions({ row, handlers }: { row: RecoverRequest; handlers: RowActionHandlers }) {
  const { t } = useTranslation()
  const id = row.objectID
  if (!id) return null

  const items: { key: string; icon: typeof List; label: string; run: () => void }[] = []
  if (handlers.onShowRunbook) items.push({
    key: 'runbook', icon: List, label: t('recoverRequests.viewRunbook', 'Runbook'),
    run: () => handlers.onShowRunbook!(id, row.scenario ?? null, row.forecastStartTime ?? null) })
  if (handlers.onShowObjectDetails) items.push({
    key: 'details', icon: FileStack, label: t('recoverRequests.objectDetails', 'Object details'),
    run: () => handlers.onShowObjectDetails!(id, String(row.requestID ?? '')) })
  if (handlers.onShowFloorPlan) items.push({
    key: 'floor', icon: Map, label: t('recoverRequests.floorPlan', 'Floor plan'),
    run: () => handlers.onShowFloorPlan!(id) })
  if (handlers.onShowSubObjectProgress) items.push({
    key: 'sub', icon: ListTree, label: t('recoverRequests.subObjects', 'Sub-objects'),
    run: () => handlers.onShowSubObjectProgress!(id, row.objectDescription ?? null) })
  if (handlers.onShowQRCode) items.push({
    key: 'qr', icon: QrCode, label: t('recoverRequests.qrCode', 'QR code'),
    run: () => handlers.onShowQRCode!(id) })
  if (handlers.onMarkComplete) items.push({
    key: 'done', icon: CheckCircle2, label: t('recoverRequests.markComplete', 'Mark complete'),
    run: () => handlers.onMarkComplete!(id, row.objectDescription ?? null, row.id ?? null) })

  return (
    <span className="flex items-center gap-0">
      {items.map(({ key, icon: Icon, label, run }) => (
        <Button
          key={key}
          variant="ghost"
          size="sm"
          className="h-7 w-5 min-w-0 flex-none p-0 text-blue-600 hover:text-blue-700"
          title={label}
          aria-label={label}
          onClick={(e) => { e.stopPropagation(); run() }}
        >
          <Icon className="h-3.5 w-3.5" />
        </Button>
      ))}
    </span>
  )
}
